import { supabase } from "@/lib/supabase";

export interface MediaAsset {
  id: string;
  org_id: string;
  storage_key: string;
  original_filename: string;
  mime_type: string;
  media_type: string;
  file_size: number;
  width: number;
  height: number;
  checksum: string;
  status: string;
  created_at: string;
}

export interface ProductMedia {
  id: string;
  product_id: string;
  media_asset_id: string;
  role: MediaRole;
  position: number;
  is_primary: boolean;
  alt_text: string;
  status: string;
  media_assets?: MediaAsset;
}

export interface VariantMedia {
  id: string;
  variant_id: string;
  media_asset_id: string;
  role: MediaRole;
  position: number;
  is_primary: boolean;
  alt_text: string;
  status: string;
  media_assets?: MediaAsset;
}

export interface OptionValueMedia {
  id: string;
  product_option_value_id: string;
  media_asset_id: string;
  role: MediaRole;
  position: number;
  is_primary: boolean;
  alt_text: string;
  status: string;
  media_assets?: MediaAsset;
}

export type MediaRole = "gallery" | "detail" | "lifestyle" | "packaging" | "size_chart" | "swatch";

export const MEDIA_ROLE_LABELS: Record<MediaRole, string> = {
  gallery: "Gallery",
  detail: "Detail",
  lifestyle: "Lifestyle",
  packaging: "Packaging",
  size_chart: "Size Chart",
  swatch: "Swatch",
};

export const VALID_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export function validateFile(file: File): string | null {
  if (file.size === 0) return "File is empty.";
  if (file.size > MAX_FILE_SIZE)
    return `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 10 MB.`;
  if (!VALID_IMAGE_TYPES.includes(file.type))
    return `Unsupported format: ${file.type}. Use JPEG, PNG, or WebP.`;
  return null;
}

export async function computeChecksum(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image dimensions."));
    };
    img.src = url;
  });
}

export async function signMediaUrl(storageKey: string, expiresIn = 3600): Promise<string | null> {
  if (!isPrivateProductMediaKey(storageKey)) return operatorMediaSrc(storageKey);
  const { data, error } = await supabase.storage.from("product-media").createSignedUrl(storageKey, expiresIn);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

export async function signMediaUrls(storageKeys: string[], expiresIn = 3600): Promise<Record<string, string>> {
  const map: Record<string, string> = {};
  const privateKeys = [...new Set(storageKeys.filter(isPrivateProductMediaKey))];
  for (const key of storageKeys) {
    if (!isPrivateProductMediaKey(key)) {
      const remote = operatorMediaSrc(key);
      if (remote) map[key] = remote;
    }
  }
  await Promise.all(privateKeys.map(async (key) => {
    const { data, error } = await supabase.storage.from("product-media").createSignedUrl(key, expiresIn);
    if (!error && data?.signedUrl) map[key] = data.signedUrl;
  }));
  return map;
}

/** Absolute http(s) keys are imported remote URLs, not private bucket objects. */
export function isPrivateProductMediaKey(storageKey: string | null | undefined): boolean {
  if (!storageKey) return false;
  return !/^https?:\/\//i.test(storageKey.trim());
}

/** Operator preview src: signed private objects, or imported remote URL as-is. Never publishes. */
export function operatorMediaSrc(storageKey: string | null | undefined): string | null {
  if (!storageKey) return null;
  const key = storageKey.trim();
  if (/^https?:\/\//i.test(key)) return key;
  return null;
}

export function uniqueOptionValueMedia<T extends { id: string; media_asset_id?: string }>(rows: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of rows) {
    const key = row.media_asset_id ? `${row.media_asset_id}` : row.id;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

/**
 * New id order after moving one media row within its OWN list (product gallery,
 * option-value media, or variant media). Out-of-range moves — including a stale
 * index from a filter/page change — return the original order unchanged, so an
 * invalid move can never shuffle a list. Never mutates the input array.
 *
 * Callers must pass the list that actually backs the card being moved; passing a
 * different list's rows (e.g. the product gallery for a variant card) writes
 * positions to the wrong table by the wrong ids and silently does nothing.
 */
export function reorderedMediaIds<T extends { id: string }>(
  items: T[],
  fromIdx: number,
  toIdx: number,
): string[] {
  if (fromIdx < 0 || fromIdx >= items.length || toIdx < 0 || toIdx >= items.length) {
    return items.map((item) => item.id);
  }
  const reordered = [...items];
  const [moved] = reordered.splice(fromIdx, 1);
  reordered.splice(toIdx, 0, moved);
  return reordered.map((item) => item.id);
}

export function productMediaStoragePath(
  orgId: string,
  owner: { productId: string } | { draftId: string },
  fileName: string,
  now = Date.now(),
): string {
  const safeName = fileName
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 60);
  const ext = fileName.split(".").pop()?.toLowerCase() || "jpg";
  if ("draftId" in owner) return `${orgId}/drafts/${owner.draftId}/${now}-${safeName}`;
  return `${orgId}/${owner.productId}/${now}-${safeName}`;
}

export const SIGNED_MEDIA_REFRESH_MS = 45 * 60 * 1000;

async function insertMediaAssetFromFile(
  orgId: string,
  owner: { productId: string } | { draftId: string },
  file: File,
): Promise<MediaAsset> {
  const validationError = validateFile(file);
  if (validationError) throw new Error(validationError);

  const [checksum, dimensions] = await Promise.all([
    computeChecksum(file),
    getImageDimensions(file),
  ]);

  const { data: existing } = await supabase
    .from("media_assets")
    .select("id, storage_key, status, org_id")
    .eq("org_id", orgId)
    .eq("checksum", checksum)
    .maybeSingle();

  if (existing && existing.status !== "archived") {
    return existing as MediaAsset;
  }

  const path = productMediaStoragePath(orgId, owner, file.name);

  const { error: upErr } = await supabase.storage.from("product-media").upload(path, file, {
    contentType: file.type,
    upsert: false,
  });
  if (upErr) throw upErr;

  const { data: assetData, error: insErr } = await supabase
    .from("media_assets")
    .insert({
      org_id: orgId,
      storage_key: path,
      original_filename: file.name,
      mime_type: file.type,
      media_type: "image",
      file_size: file.size,
      width: dimensions.width,
      height: dimensions.height,
      checksum,
      status: "active",
    })
    .select()
    .single();

  if (insErr || !assetData) throw insErr ?? new Error("Failed to record media asset.");
  return assetData as MediaAsset;
}

export async function uploadMediaAsset(
  orgId: string,
  productId: string,
  file: File,
): Promise<MediaAsset> {
  return insertMediaAssetFromFile(orgId, { productId }, file);
}

export async function uploadDraftMediaAsset(
  orgId: string,
  draftId: string,
  file: File,
): Promise<MediaAsset> {
  return insertMediaAssetFromFile(orgId, { draftId }, file);
}

export async function removePrivateMediaObjects(storageKeys: string[]): Promise<void> {
  const keys = storageKeys.filter((key) => isPrivateProductMediaKey(key));
  if (keys.length === 0) return;
  const { error } = await supabase.storage.from("product-media").remove(keys);
  if (error) throw error;
}

/**
 * Purge archived catalogue media after a product/variant hard delete.
 * The Edge Function rechecks org ownership and all live media references before
 * deleting private/public storage bytes, so a shared asset can never be removed
 * merely because one catalogue owner disappeared.
 */
export async function cleanupCatalogueMediaAssets(orgId: string, mediaAssetIds: string[]): Promise<void> {
  const ids = [...new Set(mediaAssetIds.filter(Boolean))];
  if (ids.length === 0) return;
  const { data, error } = await supabase.functions.invoke("catalogue-media-cleanup", {
    body: { org_id: orgId, media_asset_ids: ids },
  });
  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || "Catalogue media cleanup failed.");
}

export async function assignProductMedia(
  orgId: string,
  productId: string,
  mediaAssetId: string,
  role: MediaRole = "gallery",
  altText = "",
  position = 0,
  isPrimary = false,
): Promise<void> {
  const { error } = await supabase.from("product_media").insert({
    org_id: orgId,
    product_id: productId,
    media_asset_id: mediaAssetId,
    role,
    alt_text: altText,
    position,
    status: "active",
    // `is_primary` is the single authoritative primary-image flag (retrieval,
    // the Media tab, and set_product_primary_media all key off it). `role` is
    // a purely categorical label and is never used to represent primary state,
    // so the two can no longer contradict each other.
    is_primary: isPrimary,
  });
  if (error) throw error;
}

export async function assignVariantMedia(
  orgId: string,
  variantId: string,
  mediaAssetId: string,
  role: MediaRole = "gallery",
  altText = "",
  position = 0,
): Promise<void> {
  const { error } = await supabase.from("variant_media").insert({
    org_id: orgId,
    variant_id: variantId,
    media_asset_id: mediaAssetId,
    role,
    alt_text: altText,
    position,
    status: "active",
  });
  if (error) throw error;
}

export async function assignOptionValueMedia(
  orgId: string,
  optionValueId: string,
  mediaAssetId: string,
  role: MediaRole = "gallery",
  altText = "",
  position = 0,
): Promise<void> {
  const { error } = await supabase.from("option_value_media").insert({
    org_id: orgId,
    product_option_value_id: optionValueId,
    media_asset_id: mediaAssetId,
    role,
    alt_text: altText,
    position,
    status: "active",
  });
  if (error) throw error;
}

export async function removeProductMediaAssignment(orgId: string, productMediaId: string): Promise<void> {
  const { error } = await supabase.from("product_media").delete().eq("id", productMediaId).eq("org_id", orgId);
  if (error) throw error;
}

export async function removeVariantMediaAssignment(orgId: string, variantMediaId: string): Promise<void> {
  const { error } = await supabase.from("variant_media").delete().eq("id", variantMediaId).eq("org_id", orgId);
  if (error) throw error;
}

export async function removeOptionValueMediaAssignment(orgId: string, id: string): Promise<void> {
  const { error } = await supabase.from("option_value_media").delete().eq("id", id).eq("org_id", orgId);
  if (error) throw error;
}

export async function updateMediaAltText(
  table: "product_media" | "variant_media" | "option_value_media",
  orgId: string,
  id: string,
  altText: string,
): Promise<void> {
  const { error } = await supabase.from(table).update({ alt_text: altText }).eq("id", id).eq("org_id", orgId);
  if (error) throw error;
}

export async function updateMediaRole(
  table: "product_media" | "variant_media" | "option_value_media",
  orgId: string,
  id: string,
  role: MediaRole,
): Promise<void> {
  const { error } = await supabase.from(table).update({ role }).eq("id", id).eq("org_id", orgId);
  if (error) throw error;
}